import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { forgeClassHelper } from '../utils/classes';
import * as ReactabularTable from 'reactabular-table';
import SortableTableHeader from './_SortableHeader';

const classes = forgeClassHelper('table');

/** Comparison function for string values
 *
 * Returns:
 *   -1 for valueA sorted before valueB
 *   0 for valueA retains existing sort order relative to valueB
 *   1 for valueA sorted after valueB
 */
function compareString(valueA, valueB) {
  return valueA.localeCompare(valueB);
}

/** Comparison function for numeric values
 *
 * Returns:
 *   -1 for valueA sorted before valueB
 *   0 for valueA retains existing sort order relative to valueB
 *   1 for valueA sorted after valueB
 */

function compareNumeric(valueA, valueB) {
  return valueA - valueB;
}

/** Comparison function that auto-detects what comparison method to use
 * based on the types of the values presented.
 *
 * If valueA and valueB are mixed types or objects, then assume they retain their
 * existing sort order.
 *
 * Returns:
 *   -1 for valueA sorted before valueB
 *   0 for valueA retains existing sort order relative to valueB
 *   1 for valueA sorted after valueB
 */
function compare(valueA, valueB) {
  if (typeof valueA === 'number' && typeof valueB === 'number') {
    // Sort by number if both values are numbers
    return compareNumeric(valueA, valueB);
  } else if (typeof valueA === 'string' && typeof valueB === 'string') {
    // Sort by string if both values are strings
    return compareString(valueA, valueB);
  } else {
    // Data are either mixed types or objects. Cannot compare,
    // so assume they retain the current sort order relative to each other.
    return 0;
  }
}

/** Returns row data sorted based on the current sortOrder and whether auto
 * sorting is enabled. */
function sortRows({ columns, rows, sortOrder, autoSort }) {
  const shouldSort = sortOrder && Object.keys(sortOrder).length > 0 && autoSort;
  if (shouldSort) {
    const sortColumn = columns.find((column) => column.key === sortOrder.column);
    const sortBy = () => {
      if (sortColumn.sortable.reducer) {
        return (a, b) => {
          const valueA = sortColumn.sortable.reducer(a[sortColumn.key]);
          const valueB = sortColumn.sortable.reducer(b[sortColumn.key]);

          return compare(valueA, valueB);
        };
      } else if (sortColumn.numeric) {
        return (a, b) => {
          const valueA = a[sortColumn.key];
          const valueB = b[sortColumn.key];
          return compareNumeric(valueA, valueB);
        };
      } else {
        return (a, b) => {
          const valueA = a[sortColumn.key];
          const valueB = b[sortColumn.key];

          return compare(valueA, valueB);
        };
      }
    };

    const sortedRows = [...rows].sort(sortBy());
    return sortOrder.direction === 'asc' ? sortedRows : sortedRows.reverse();
  } else {
    return rows;
  }
}

const Table = ({
  autoSort,
  className,
  columns,
  rows,
  rowKey,
  showHover,
  sortOrder: upstreamSortOrder,
  onSort,
  children,
  replaceChildren,
  onRow,
  renderers,
  layout,
  nowrapHeaders,
  ...restProps
}) => {
  const [sortOrder, setSortOrder] = useState(upstreamSortOrder || {});

  useEffect(() => {
    if (upstreamSortOrder) {
      setSortOrder(upstreamSortOrder);
    }
  }, [upstreamSortOrder]);

  const getAriaSortOrder = (column) => {
    if (column.sortable) {
      return sortOrder.column === column.key
        ? { 'aria-sort': sortOrder.direction === 'asc' ? 'ascending' : 'descending' }
        : { 'aria-sort': 'none' };
    }
  };

  const getDirection = (column) => {
    if (sortOrder.column === column) {
      return sortOrder.direction;
    } else {
      return undefined;
    }
  };

  const handleSort = (column, direction) => {
    const newSortOrder = direction ? { column, direction } : {};

    if (onSort) {
      onSort(newSortOrder);
    }

    setSortOrder(newSortOrder);
  };

  const passOnColumns = columns
    ? columns.map((column) => {
        // merge the forge header with any overrides the user provides
        const mergedHeader = {
          // header defaults that can get overridden by the user
          label: column.displayName,
          ...column.header,
        };
        const columnHeaderProps = column.header && column.header.props;
        const ariaSortOrder = getAriaSortOrder(column);
        const mergedHeaderClasses = classes({
          element: 'header',
          modifiers: nowrapHeaders ? 'no-wrap' : null,
          extra: column.header && column.header.props && column.header.props.className,
        }).className;
        mergedHeader.props = {
          // header props defaults that can get overridden by the user
          'data-column': column.key,
          scope: 'col',
          role: 'columnheader',
          title: nowrapHeaders ? column.displayName : null,
          ...ariaSortOrder,
          ...columnHeaderProps,
          className: mergedHeaderClasses,
        };
        const forgeHeaderFormatters = column.sortable
          ? [
              (label) => (
                <SortableTableHeader
                  text={label}
                  onSort={(direction) => {
                    handleSort(column.key, direction);
                  }}
                  direction={getDirection(column.key)}
                />
              ),
            ]
          : [];
        mergedHeader.formatters =
          column.header && column.header.formatters
            ? forgeHeaderFormatters.concat(column.header.formatters)
            : forgeHeaderFormatters;

        // merge the forge cell with any overrides the user provides
        const mergedCell = { ...column.cell };
        const mergedCellClasses = classes({
          element: 'cell',
          modifiers: { numeric: column.numeric },
          extra: column.cell && column.cell.props && column.cell.props.className,
        }).className;
        const columnCellProps = column.cell && column.cell.props;
        mergedCell.props = {
          // cell props defaults that can get overridden by the user (not including className)
          'data-column': column.key,
          ...columnCellProps,
        };
        mergedCell.props.className = mergedCellClasses;
        const forgeCellFormatters = column.template ? [column.template] : [];
        mergedCell.formatters =
          column.cell && column.cell.formatters
            ? forgeCellFormatters.concat(column.cell.formatters)
            : forgeCellFormatters;

        return {
          props: column.props,
          property: column.key,
          header: mergedHeader,
          cell: mergedCell,
        };
      })
    : [];

  const sortedRows = sortRows({ columns, rows, sortOrder, autoSort });

  return (
    <ReactabularTable.Provider
      {...restProps}
      columns={passOnColumns}
      renderers={renderers}
      {...classes({
        extra: className,
        modifiers: {
          'show-hover': showHover,
          [layout]: layout,
        },
      })}
    >
      {!replaceChildren && <ReactabularTable.Header />}
      {!replaceChildren && <ReactabularTable.Body onRow={onRow} rows={sortedRows} rowKey={rowKey} />}
      {children}
    </ReactabularTable.Provider>
  );
};

Table.defaultProps = {
  autoSort: true,
  rowKey: 'id',
  showHover: true,
  onRow: () => {},
  replaceChildren: false,
  layout: 'medium',
  nowrapHeaders: false,
};

Table.propTypes = {
  /** Automatically sort rows in the table. This is typically disabled for Tables
   * controlled by a Paginator, as these use-cases
   * often use the onSort prop to provide row data in sorted order.
   */
  autoSort: PropTypes.bool,
  /**
   * Defines the columns. Must include displayName and unique key.
   * Can also:
   *  - Specify the column as numeric to right-align content
   *  - Set a custom template (See [formatters](https://reactabular.js.org/#/column-definition/formatters))
   *  - Define a column as sortable with an optional reducer function for reducing complex values to a string or number
   *  - Override the `props`, `header`, or `cell` portions of the [column definition](https://reactabular.js.org/#/column-definition)
   */
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      displayName: PropTypes.string.isRequired,
      key: PropTypes.string.isRequired,
      numeric: PropTypes.bool,
      template: PropTypes.func,
      sortable: PropTypes.oneOfType([
        PropTypes.shape({
          reducer: PropTypes.func,
        }),
        PropTypes.bool,
      ]),
      props: PropTypes.object,
      header: PropTypes.object,
      cell: PropTypes.object,
    })
  ).isRequired,
  /** Defines the rows as objects. Each key should match a column's key to display the value in that column. */
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** By default, this will add children to the table body - to completely replace the table header and body, set
   * `replaceChildren` to true */
  children: PropTypes.node,
  /** Adds a class to the root element of the component */
  className: PropTypes.string,
  /** Sets the table layout size */
  layout: PropTypes.oneOf(['compact', 'medium']),
  /** Forces column headers on to a single line and truncates with ellipsis */
  nowrapHeaders: PropTypes.bool,
  /** Function that runs on each row as it is rendered – should return an object of props to be added to the row */
  onRow: PropTypes.func,
  /**
   * Called with the new sort order when sorting changes.
   * Sort order has same shape as `sortOrder` prop or undefined for no sorting.
   */
  onSort: PropTypes.func,
  /** Overrides default elements in the rendered table — see the [Reactabular docs](https://reactabular.js.org/#/table/overriding-default-renderers)
   * for more info */
  renderers: PropTypes.object,
  /** If true, this will replace the default table header and body with whatever `children` you pass to `Table` -
   * otherwise, `children` are just appended after the table body */
  replaceChildren: PropTypes.bool,
  /** Identifies which property to use as each row's key. */
  rowKey: PropTypes.string,
  /** Indicates whether to show hover states for improved reading across a single row */
  showHover: PropTypes.bool,
  /**
   * Sets the order of the rows including the column it's sorted on and whether it's in ascending or descending order.
   * Leave `undefined` to let Table handle the sorting. Set to `null` to force the order in the `rows` prop.
   */
  sortOrder: function (props, propName, ...rest) {
    // Allow null and undefined (and other falsy values)
    if (!props[propName]) {
      return;
    }

    const typeError = PropTypes.shape({
      column: PropTypes.string.isRequired,
      direction: PropTypes.oneOf(['asc', 'desc']),
    })(props, propName, ...rest);

    if (typeError) {
      return typeError;
    }

    const column = props.columns.find((column) => column.key === props[propName].column);

    if (!column || !column.sortable) {
      return new Error(`sortOrder.column must be the key of a sortable column: props[propName].column`);
    }
  },
  /** Passthrough props to the root component [`ReactabularTable.Provider`](https://reactabular.js.org/#/table/components?a=tableprovider) that wraps the `Table` */
  '...rest': PropTypes.any,
};

export default Table;
